import { Grid, Typography, Box, Link } from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { Fragment, useEffect, useState } from "react";

import pages from "../../constants/pages";
import OTPVerification from "./OTPVerification";
import { useAxios, useAlert } from "../../hooks";
import MobileNumberInput from "./MobileNumberInput";
import { SubheaderSection } from "./../../components";
import Error from "../Error/Error";
import EventBookingSuccess from "../Success/EventBookingSuccess";
import { signInMember } from "../../redux/slices/memberSlice";
import icon from "./assests/icon.svg";
import ResendOTPButton from "./../../components/ResendOTPButton";
import WrongPhoneNumberDialog from "./WrongPhoneNumberDialog";

const MobileVerification = ({
  selectedOption,
  numberOfInputs = 4,
  setProcced,
}) => {
  const axios = useAxios();
  const { alert } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { eventID } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const visitorID = queryParams.get("visitor_id");
  const userTypeID = queryParams.get("user_type_id");
  const isGuardLogin = queryParams.get("guard_login");
  const getUuid = () => {
    const uuidFromUrl = queryParams.get("uuid");
    if (uuidFromUrl) return uuidFromUrl;
    try {
      const previousPage = sessionStorage.getItem("previousPage");
      if (previousPage) {
        const urlObj = new URL(previousPage, window.location.origin);
        return urlObj.searchParams.get("uuid");
      }
    } catch (e) {
      console.error("Error parsing previousPage for uuid:", e);
    }
    return null;
  };
  const uuid = getUuid();
  const [loading, setLoading] = useState(false);
  const [userConsent, setUserConsent] = useState(1);
  const [eventDetail, setEventDetail] = useState(false);
  const [bannerImage, setBannerImage] = useState("");
  const [whatsappCheck, setWhatsappCheck] = useState(true);
  const [otp, setOTP] = useState(new Array(numberOfInputs).fill(""));
  const [mobileNumberOtpGenrated, setMobileNumberOtpGenrated] = useState(
    queryParams.get("otp_requested") === "true" || false
  );
  const [mobileNumber, setMobileNumber] = useState(
    queryParams.get("mobile") ||
      (sessionStorage.getItem("mobile") ? sessionStorage.getItem("mobile") : "")
  );
  const [otpMatch, setOTPMatch] = useState(true);
  const [open, setOpen] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    queryParams.get("country_code") || "IN"
  );

  const getSelectedDialCode = () => {
    const selectedCountry = countryCodes.find(
      (c) => (c.alpha2_code || c.code || c.country_code) === selectedCountryCode
    );
    return selectedCountry
      ? String(selectedCountry.dial_code || "").replace("+", "")
      : "91";
  };

  const getSelectedAlpha2Code = () => {
    const selectedCountry = countryCodes.find(
      (c) => (c.alpha2_code || c.code || c.country_code) === selectedCountryCode
    );
    return selectedCountry
      ? selectedCountry.alpha2_code || ""
      : selectedCountryCode;
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleOtpGoBack = () => {
    setMobileNumberOtpGenrated(false);
  };

  const handleUserConsentChange = (event) => {
    const isChecked = event.target.checked;

    const consentValue = isChecked ? "1" : "0";
    setUserConsent(consentValue);
  };

  const handleOTPSubmit = () => {
    const otpString = otp.join("");
    const numericOtpString = otpString.replace(/\D/g, "");
    const numericOtp = parseInt(numericOtpString, 10);

    if (isNaN(numericOtp) || numericOtpString.length !== 4) {
      dispatch(
        alert({
          type: "error",
          message: "Invalid OTP. Please enter a 4-digit numeric code.",
        })
      );
      return;
    }

    setLoading(true);

    const dialCode = getSelectedDialCode();
    const countryCode = getSelectedAlpha2Code();

    axios({
      url: `event/booking/verify-otp?mobile_no=${mobileNumber}&otp=${numericOtp}&country_code=${countryCode}${
        whatsappCheck ? "&channel=whatsapp" : ""
      }`,
      method: "POST",
      disableRedirect: true,
    })
      .then((response) => {
        if (response.status) {
          axios({
            url: `notification/chrome_notification/update-user-token`,
            method: "POST",
            data: {
              token: sessionStorage.getItem("token"),
              visitor_id: response.data?._id,
            },
          });

          dispatch(
            signInMember({
              authToken: response.data.auth_token,
              ID: response.data._id,
              expiryTimestamp: response.data.expiry_timestamp,
            })
          )
            .then(() => {
              setLoading(false);
              localStorage.setItem("mobileNumber", dialCode + mobileNumber);
              if (
                sessionStorage.getItem("previousPage") == "/login" ||
                sessionStorage.getItem("previousPage") ==
                  "/login?guard_login=true" ||
                !sessionStorage.getItem("previousPage")
              ) {
                navigate(pages.home.route);
              } else {
                navigate(sessionStorage.getItem("previousPage"));
              }
            })
            .catch((error) => {
              setLoading(false);
              console.error("Error during signInMember dispatch:", error);
            });
        } else {
          setOTPMatch(false);
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setOTPMatch(false);
        console.error("Error sending OTP:", error);
      });
  };

  const handleResendOTP = (value) => {
    setLoading(true);
    setWhatsappCheck(value ? value : "");
    setOTP(new Array(numberOfInputs).fill(""));
    const dialCode = getSelectedDialCode();
    const countryCode = getSelectedAlpha2Code();
    axios({
      url: `event/booking/resend-otp?mobile_no=${mobileNumber}&country_code=${countryCode}${
        value ? `&channel=${value}` : ""
      }`,
      method: "POST",
    })
      .then((response) => {
        if (response.status) {
          dispatch(
            alert({
              type: "success",
              message: response.message.displayMessage,
            })
          );

          setMobileNumberOtpGenrated(true);
        }
      })
      .catch((error) => {
        console.error("Error sending OTP:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleGetOTP = () => {
    setOTP(new Array(numberOfInputs).fill(""));
    setLoading(true);

    // Booking lookup disabled for now — skip `event/visitor/check-booking` and
    // go straight to sending the OTP. Set to true to re-enable.
    const CHECK_BOOKING_ENABLED = false;

    if (CHECK_BOOKING_ENABLED && eventID !== "bookappointment") {
      const dialCode = getSelectedDialCode();
      const countryCode = getSelectedAlpha2Code();
      axios({
        url:
          "event/visitor/check-booking?event_id=" +
          eventID +
          "&mobile_number=" +
          mobileNumber +
          "&country_code=" +
          countryCode,
        disableRedirect: true,
        disableAlert: false,
      })
        .then((response) => {
          if (response.status) {
            if (response.data.length > 0) {
              if (response.data[0].payment_status === "Pending") {
                navigate(
                  pages.EventBooking.route +
                    "/" +
                    response.data[0]?.event_id +
                    "/" +
                    response.data[0]?._id,
                  {
                    state: {
                      userData: response.data,
                    },
                  }
                );
              }

              if (response.data[0].payment_status === "Completed") {
                navigate(
                  pages.EventSummary.route +
                    response.data[0].event_id +
                    "/" +
                    response.data[0]._id,
                  {
                    state: {
                      userData: response.data,
                    },
                  }
                );
              }

              setLoading(false);
            } else {
              setOTP(new Array(numberOfInputs).fill(""));
              setLoading(true);

              const dialCode = getSelectedDialCode();
              const countryCode = getSelectedAlpha2Code();
              axios({
                url: `event/booking/send-otp?mobile_no=${mobileNumber}&country_code=${countryCode}${
                  whatsappCheck ? "&channel=whatsapp" : ""
                }`,
                method: "POST",
                disableRedirect: true,
              })
                .then((resp) => {
                  if (resp.status) {
                    dispatch(
                      alert({
                        type: "success",
                        message: resp.message.displayMessage,
                      })
                    );
                    setMobileNumberOtpGenrated(true);
                  } else {
                    navigate(
                      "/visitor-sign-up" +
                        (isGuardLogin == "true" ? "?guard_login=true" : "")
                    );
                    // dispatch(
                    //   alert({
                    //     type: "error",
                    //     message:
                    //       response?.data?.message || "Failed to send OTP",
                    //   })
                    // );
                  }
                  setLoading(false);
                })
                .catch((error) => {
                  console.error("Error sending OTP:", error);
                  // dispatch(
                  //   alert({
                  //     type: "error",
                  //     message:
                  //       error?.response?.data?.message || "Failed to send OTP",
                  //   })
                  // );
                  setLoading(false);
                })
                .finally(() => {
                  setLoading(false);
                });
            }
          } else {
            setLoading(false);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log(error, "error");
        });
    } else {
      const dialCode = getSelectedDialCode();
      const countryCode = getSelectedAlpha2Code();
      axios({
        url: `event/booking/send-otp?mobile_no=${mobileNumber}&country_code=${countryCode}${
          whatsappCheck ? "&channel=whatsapp" : ""
        }`,
        method: "POST",
        disableRedirect: true,
      })
        .then((resp) => {
          if (resp.status) {
            dispatch(
              alert({
                type: "success",
                message: resp.message.displayMessage,
              })
            );
            setMobileNumberOtpGenrated(true);
          } else {
            navigate("/visitor-sign-up");
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error sending OTP:", error);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (eventID && eventID !== "bookappointment" && userTypeID == 1) {
      axios({
        url: "admin/events/event-details?event_id=" + eventID,
        method: "GET",
        customAuthToken: "9beaa95b-c59f-4ec9-bb60-5f4ee1986311",
      }).then((response) => {
        if (response.status) {
          response?.data?.button_color &&
            sessionStorage.setItem("buttonColor", response.data.button_color);
          response?.data?.background_color &&
            sessionStorage.setItem(
              "buttonBackground",
              response.data.background_color
            );
          setEventDetail(response.data);
        }
      });
    }

    axios({
      url: "event/booking/country-codes",
      method: "GET",
      disableRedirect: true,
      disableError: true,
    })
      .then((response) => {
        if (response && response.status && response.data) {
          const list = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data.data)
            ? response.data.data
            : [];
          setCountryCodes(list);
        }
      })
      .catch((error) => {
        console.error("Error fetching country codes:", error);
      });
  }, []);

  useEffect(() => {
    try {
      const previousPage = sessionStorage.getItem("previousPage");
      if (previousPage) {
        // Handle relative URLs by providing a base
        const urlObj = new URL(previousPage, window.location.origin);
        const uuid = urlObj.searchParams.get("uuid");

        if (uuid) {
          setLoading(true);
          axios({
            url: `notification/otp/details?uuid=${uuid}`,
            method: "GET",
            disableRedirect: true,
            disableError: true,
          })
            .then((response) => {
              if (response.data.data?.mobile_number || response.data?.mobile) {
                let fetchedMobile = String(
                  response.data.data.mobile_number || response.data.mobile
                );
                let matchedCode = "";
                if (countryCodes && countryCodes.length > 0) {
                  const sortedCodes = [...countryCodes].sort((a, b) => {
                    const codeA = String(
                      a.dial_code || a.country_code || a.code || ""
                    ).replace("+", "");
                    const codeB = String(
                      b.dial_code || b.country_code || b.code || ""
                    ).replace("+", "");
                    return codeB.length - codeA.length;
                  });
                  for (const c of sortedCodes) {
                    const codeVal = String(
                      c.dial_code || c.country_code || c.code || ""
                    ).replace("+", "");
                    if (codeVal && fetchedMobile.startsWith(codeVal)) {
                      matchedCode = codeVal;
                      setSelectedCountryCode(
                        c.alpha2_code || c.code || c.country_code || codeVal
                      );
                      break;
                    }
                  }
                }
                if (!matchedCode) {
                  const cleanCode = selectedCountryCode.replace("+", "");
                  if (fetchedMobile.startsWith(cleanCode)) {
                    matchedCode = cleanCode;
                  }
                }
                // if (matchedCode) {
                //   fetchedMobile = fetchedMobile.slice(matchedCode.length);
                // } else {
                //   fetchedMobile = fetchedMobile.slice(2);
                // }
                setMobileNumber(fetchedMobile);
                sessionStorage.setItem("mobile", fetchedMobile);
              }
            })
            .catch((err) => {
              console.error("Error fetching details for UUID:", err);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      }
    } catch (error) {
      console.error("Error checking previousPage for UUID:", error);
    }
  }, []);

  const otpSentRef = React.useRef(false);

  useEffect(() => {
    if (
      queryParams.get("otp_requested") === "true" &&
      mobileNumber &&
      countryCodes.length > 0 &&
      !otpSentRef.current
    ) {
      otpSentRef.current = true;

      // Clean up search parameters so refreshing won't trigger another OTP
      const params = new URLSearchParams(window.location.search);
      params.delete("otp_requested");
      const newSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true }
      );

      setOTP(new Array(numberOfInputs).fill(""));
      setLoading(true);

      const dialCode = getSelectedDialCode();
      const countryCode = getSelectedAlpha2Code();
      axios({
        url: `event/booking/send-otp?mobile_no=${mobileNumber}&country_code=${countryCode}${
          whatsappCheck ? "&channel=whatsapp" : ""
        }`,
        method: "POST",
        disableRedirect: true,
      })
        .then((resp) => {
          if (resp.status) {
            dispatch(
              alert({
                type: "success",
                message: resp.message.displayMessage,
              })
            );
            setMobileNumberOtpGenrated(true);
          } else {
            navigate("/visitor-sign-up");
          }
        })
        .catch((error) => {
          console.error("Error sending OTP automatically:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCodes, mobileNumber]);

  const handleRelogin = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <Grid container flexDirection={"column"}>
        <Grid item container className="eventImageBox">
          <div style={{ width: "100%", position: "fixed", overflow: "hidden" }}>
            <img src={icon} style={{ width: "100%", height: "37vh" }} />
          </div>
        </Grid>

        {mobileNumberOtpGenrated ? (
          <Grid item padding={"0px"}>
            <Grid item container justifyContent={"center"}>
              <Grid
                item
                md={12}
                sl={6}
                zIndex={99999}
                marginTop={"30vh"}
                sx={{
                  alignContent: "center",
                  width: "100%",
                  padding: "0px 25px",
                  height: "64vh",

                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Grid item container justifyContent={"center"}>
                  <SubheaderSection
                    textAlign="start"
                    title="Enter OTP"
                    content={`sent to +${selectedCountryCode.replace(
                      "+",
                      ""
                    )} <b> ${mobileNumber}</b>`}
                  />

                  <OTPVerification
                    handleOtpGoBack={handleOtpGoBack}
                    loading={loading}
                    otpLength={numberOfInputs}
                    onSubmit={handleOTPSubmit}
                    onResend={handleResendOTP}
                    setOTP={setOTP}
                    otp={otp}
                    otpMatch={otpMatch}
                    setOTPMatch={setOTPMatch}
                  />
                </Grid>

                <Grid item container>
                  <ResendOTPButton
                    onClick={handleResendOTP}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item padding={"0px"}>
            <Grid item container justifyContent={"center"}>
              <Grid
                item
                md={12}
                sl={6}
                zIndex={99999}
                marginTop={"22vh"}
                sx={{
                  alignContent: "center",
                  width: "100%",
                  padding: "0px 25px",
                  height: "64vh",

                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Grid
                  item
                  container
                  sx={{
                    justifyContent: "center",
                  }}
                >
                  <SubheaderSection
                    textAlign="start"
                    title="Login to Digipass"
                    content={`Enter your mobile number to receive a verification code and proceed with your ${
                      eventID === "bookappointment" ? "appointment" : "booking"
                    }.`}
                  />

                  <MobileNumberInput
                    handleGoBack={handleGoBack}
                    handleUserConsentChange={handleUserConsentChange}
                    loading={loading}
                    onSubmit={handleGetOTP}
                    setMobileNumber={setMobileNumber}
                    mobileNumber={mobileNumber}
                    whatsappCheck={whatsappCheck}
                    setWhatsappCheck={setWhatsappCheck}
                    countryCodes={countryCodes}
                    selectedCountryCode={selectedCountryCode}
                    setSelectedCountryCode={setSelectedCountryCode}
                    uuid={uuid}
                  />

                  {!uuid && (
                    <Box mt={3} textAlign="center">
                      <Typography variant="body2" sx={{ color: "#757575" }}>
                        Don't have an account?{" "}
                        <Link
                          component="button"
                          type="button"
                          onClick={() =>
                            navigate(
                              "/visitor-sign-up" +
                                (isGuardLogin == "true"
                                  ? "?guard_login=true"
                                  : "")
                            )
                          }
                          sx={{
                            fontWeight: 600,
                            color: "#667eea",
                            textDecoration: "none",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Sign Up
                        </Link>
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      <WrongPhoneNumberDialog
        open={open}
        onClose={() => setOpen(false)}
        onRelogin={handleRelogin}
      />
    </Fragment>
  );
};

export default MobileVerification;
